import React from 'react';
import { useSelector } from 'react-redux';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getIsErpDriven } from 'lib/selectors/getIsErpDriven';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getHasOpenJob } from 'lib/selectors/getHasOpenJob';
import { SignedOutErpDriven } from './ERPJobControlBar/SignedOutErpDriven';
import { SignedOut } from './ERPJobControlBar/SignedOut';
import { SignedInNoJob } from './ERPJobControlBar/SignedInNoJob';
import { ActiveJobErpDriven } from './ERPJobControlBar/ActiveJobErpDriven';
import { ActiveJobPaused } from './ERPJobControlBar/ActiveJobPaused';
import { ActiveJobRunning } from './ERPJobControlBar/ActiveJobRunning';

/**
 * Control bar mode constants for easy reference and mocking
 */
export const CONTROL_BAR_MODES = {
  SIGNED_OUT_ERP_DRIVEN: 'SIGNED_OUT_ERP_DRIVEN',
  SIGNED_OUT: 'SIGNED_OUT',
  SIGNED_IN_NO_JOB: 'SIGNED_IN_NO_JOB',
  ACTIVE_JOB_ERP_DRIVEN: 'ACTIVE_JOB_ERP_DRIVEN',
  ACTIVE_JOB_PAUSED: 'ACTIVE_JOB_PAUSED',
  ACTIVE_JOB_RUNNING: 'ACTIVE_JOB_RUNNING',
};

/**
 * Determines the current control bar mode based on application state.
 * This function can be used for testing, mocking, or debugging.
 *
 * @param {Object} state - Application state object
 * @param {Object|null} state.operator - Current operator (null if signed out)
 * @param {boolean} state.hasOpenJob - Whether there's an open job
 * @param {boolean} state.isErpDriven - Whether system is in ERP-driven mode
 * @param {boolean} state.isPaused - Whether the job is paused
 * @returns {string} One of the CONTROL_BAR_MODES constants
 *
 * @example
 * // Get current mode
 * const mode = getControlBarMode({
 *   operator: null,
 *   hasOpenJob: false,
 *   isErpDriven: false,
 *   isPaused: false,
 * });
 * // Returns: 'SIGNED_OUT'
 *
 * @example
 * // Mock a specific mode for testing
 * const mockMode = getControlBarMode({
 *   operator: null,
 *   hasOpenJob: false,
 *   isErpDriven: true,
 *   isPaused: false,
 * });
 * // Returns: 'SIGNED_OUT_ERP_DRIVEN'
 */
export const getControlBarMode = ({
  operator,
  hasOpenJob,
  isErpDriven,
  isPaused,
}) => {
  // Handle cases when no operator or no open job
  if (!operator || !hasOpenJob) {
    // Signed out, ERP-driven mode - can only categorize downtime
    if (!operator && isErpDriven) {
      return CONTROL_BAR_MODES.SIGNED_OUT_ERP_DRIVEN;
    }
    // Signed out, non-ERP mode - can sign in
    if (!operator) {
      return CONTROL_BAR_MODES.SIGNED_OUT;
    }
    // Signed in but no job - can start job or categorize downtime
    return CONTROL_BAR_MODES.SIGNED_IN_NO_JOB;
  }

  // Operator has open job - determine job state
  // ERP-driven mode - limited to downtime categorization
  if (isErpDriven) {
    return CONTROL_BAR_MODES.ACTIVE_JOB_ERP_DRIVEN;
  }
  // Job is paused - can resume or sign out
  if (isPaused) {
    return CONTROL_BAR_MODES.ACTIVE_JOB_PAUSED;
  }
  // Job is actively running - full control options available
  return CONTROL_BAR_MODES.ACTIVE_JOB_RUNNING;
};

/**
 * Maps control bar mode to the corresponding React component.
 * Useful for testing or programmatic rendering.
 *
 * @param {string} mode - One of the CONTROL_BAR_MODES constants
 * @returns {React.ComponentType|null} The component for the given mode, or null if invalid
 */
export const getControlBarComponent = (mode) => {
  const componentMap = {
    [CONTROL_BAR_MODES.SIGNED_OUT_ERP_DRIVEN]: SignedOutErpDriven,
    [CONTROL_BAR_MODES.SIGNED_OUT]: SignedOut,
    [CONTROL_BAR_MODES.SIGNED_IN_NO_JOB]: SignedInNoJob,
    [CONTROL_BAR_MODES.ACTIVE_JOB_ERP_DRIVEN]: ActiveJobErpDriven,
    [CONTROL_BAR_MODES.ACTIVE_JOB_PAUSED]: ActiveJobPaused,
    [CONTROL_BAR_MODES.ACTIVE_JOB_RUNNING]: ActiveJobRunning,
  };

  return componentMap[mode] || null;
};

/**
 * Main control bar component for ERP-driven job management.
 * Acts as a state router that determines the current application state
 * and renders the appropriate state-specific control bar component.
 *
 * State determination logic:
 * - If no operator or no open job:
 *   - No operator + ERP-driven → SignedOutErpDriven
 *   - No operator → SignedOut
 *   - Operator but no job → SignedInNoJob
 * - If operator has open job:
 *   - ERP-driven mode → ActiveJobErpDriven
 *   - Job paused → ActiveJobPaused
 *   - Job running → ActiveJobRunning
 *
 * @returns {JSX.Element} The appropriate state-specific control bar component
 */
const ERPJobControlBar = () => {
  const operator = useSelector(getCurrentOperator);
  const hasOpenJob = useSelector(getHasOpenJob);
  const isErpDriven = useSelector(getIsErpDriven);
  const isPaused = useSelector(getIsPaused);

  const mode = getControlBarMode({
    operator,
    hasOpenJob,
    isErpDriven,
    isPaused,
  });

  // To test specific modes, uncomment the desired mode and comment out the getControlBarMode call above.
  // const mode = CONTROL_BAR_MODES.SIGNED_OUT_ERP_DRIVEN;
  // const mode = CONTROL_BAR_MODES.SIGNED_OUT;
  // const mode = CONTROL_BAR_MODES.SIGNED_IN_NO_JOB;
  // const mode = CONTROL_BAR_MODES.ACTIVE_JOB_ERP_DRIVEN;
  // const mode = CONTROL_BAR_MODES.ACTIVE_JOB_PAUSED;
  // const mode = CONTROL_BAR_MODES.ACTIVE_JOB_RUNNING;

  const Component = getControlBarComponent(mode);

  if (!Component) {
    // Fallback to default behavior if mode is invalid
    return null;
  }

  return <Component />;
};

export { ERPJobControlBar };
